import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Button, FormGroup, Input } from 'reactstrap';
import MultiToggle from 'react-multi-toggle';
import translate from 'containers/translate';
import PeriodStateEdit from 'components/periodStateEdit/PeriodStateEdit';
import { consumesDossier } from 'contexts/DossierContext';
import { consumesPeriods } from 'contexts/PeriodsContext';
import RemoteDataPropType from 'lib/prop-types/RemoteDataPropType';
import Constants from 'config/Constants';
import { FaExternalLinkAlt } from 'react-icons/fa';

import './DossierDetails.scss';

class DossierDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      language: this.props.dossier.language,
      notes: this.props.dossier.notes,
      name: this.props.dossier.name,
      debitorContraAccount: this.props.dossier.debitorContraAccount,
      canDeleteDossier: false,
      isTest: this.props.dossier.isTest,
      periodEndErrorMessage: '',
    };

    this.handleSaveClick = this.handleSaveClick.bind(this);
    this.handleLanguageChange = this.handleLanguageChange.bind(this);
    this.handleNotesChange = this.handleNotesChange.bind(this);
    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleDebitorAccountChange = this.handleDebitorAccountChange.bind(this);
    this.handleIsTestChange = this.handleIsTestChange.bind(this);
    this.handlePeriodEnd = this.handlePeriodEnd.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.dossier.notes !== this.props.dossier.notes) {
      this.setState({
        notes: this.props.dossier.notes,
      });
    }

    if (prevProps.dossier.language !== this.props.dossier.language) {
      this.setState({
        language: this.props.dossier.language,
      });
    }

    if (prevProps.dossier.debitorContraAccount !== this.props.dossier.debitorContraAccount) {
      this.setState({
        debitorContraAccount: this.props.dossier.debitorContraAccount,
      });
    }
  }

  render() {
    const { t } = this.props;

    const languageOptions = [
      { value: 'DE', label: 'DE' },
      { value: 'FR', label: 'FR' },
      { value: 'IT', label: 'IT' },
    ];
    const languageValue = languageOptions.find((option) => option.value === this.state.language);

    return (
      <div className={'DossierDetails'}>
        <h1>{this.props.dossier.name}</h1>
        <dl>
          {this.renderPeriodEnd()}
          <dt>{t('neon_link')}</dt>
          <dd>
            {this.props.dossier.neonId ? (
              <>
                {/*eslint-disable-next-line react/jsx-no-target-blank*/}
                <a
                  href={`${process.env.REACT_APP_NEON_PORTAL}/static/customer/${this.props.dossier.neonId}`}
                  target="_blank"
                  className="btn btn-link mb-2"
                >
                  {t('open_neon_link')}
                  <FaExternalLinkAlt className="ml-2" />
                </a>
                <br />
                <Input value={this.props.dossier.neonId} readOnly />
              </>
            ) : (
              <em>N/A</em>
            )}
          </dd>
          <dt>{t('dossier_name')}</dt>
          <Input value={this.state.name} onChange={this.handleNameChange} />
          <dt>{t('contra_account_debitor')}</dt>
          <dd>
            <Input value={this.state.debitorContraAccount} onChange={this.handleDebitorAccountChange} />
          </dd>
          <dt>{t('language')}</dt>
          <dd>
            <Select
              options={languageOptions}
              isClearable={false}
              backspaceRemovesValue={false}
              value={languageValue}
              onChange={this.handleLanguageChange}
              className="Select"
            />
          </dd>
          <dt>{t('test_dossier')}</dt>
          <MultiToggle
            options={[
              {
                displayName: t('yes'),
                value: true,
              },
              {
                displayName: t('no'),
                value: false,
              },
            ]}
            selectedOption={this.state.isTest}
            onSelectOption={this.handleIsTestChange}
          />
          <dt>{t('notes')}</dt>
          <dd>
            <FormGroup>
              <textarea className="form-control" rows="5" value={this.state.notes} onChange={this.handleNotesChange} />
            </FormGroup>
            {this.renderSaveButton()}
          </dd>
        </dl>
      </div>
    );
  }

  handleNameChange(e) {
    this.setState({
      name: e.target.value,
    });
  }

  handleDebitorAccountChange(e) {
    this.setState({
      debitorContraAccount: e.target.value,
    });
  }

  handleLanguageChange(option) {
    this.setState({ language: option.value });
  }

  handleIsTestChange(value) {
    this.setState({ isTest: value });
  }

  handlePeriodEnd() {
    const { t } = this.props;
    const period = Object.assign({}, this.props.selectedPeriod, { state: Constants.PERIOD_STATE.ARCHIVE });
    this.props.remotePeriods.api.update(period, (err) => {
      if (err && err.response && err.response.status === 409) {
        this.setState({ periodEndErrorMessage: t('err_unable_to_archive_period') });
        return;
      }
      if (!err) {
        this.props.remoteDossier.api.fetch();
      }
    });
  }

  renderPeriodEnd() {
    const { t, selectedPeriod } = this.props;
    const currentState = selectedPeriod.state;

    if (currentState !== Constants.PERIOD_STATE.CLARIFICATION) {
      return null;
    }

    return (
      <div className="mb-2">
        <PeriodStateEdit
          buttonColor="primary"
          onStateChange={this.handlePeriodEnd}
          buttonLabel={t('end_period')}
          currentState={currentState}
          targetState={Constants.PERIOD_STATE.ARCHIVE}
        />
        {this.state.periodEndErrorMessage && (
          <span className={'periodEndErrorMessage'}>{this.state.periodEndErrorMessage}</span>
        )}
      </div>
    );
  }

  handleNotesChange(e) {
    this.setState({
      notes: e.target.value,
    });
  }

  renderSaveButton() {
    const { t, dossier } = this.props;
    let disabled = false;

    if (
      dossier.notes === this.state.notes &&
      dossier.name === this.state.name &&
      dossier.language === this.state.language &&
      dossier.debitorContraAccount === this.state.debitorContraAccount &&
      dossier.isTest === this.state.isTest
    ) {
      disabled = true;
    }

    return (
      <Button type="button" onClick={this.handleSaveClick} color="primary" disabled={disabled}>
        {t('save')}
      </Button>
    );
  }

  handleSaveClick() {
    const dossier = {
      ...this.props.dossier,
      language: this.state.language,
      notes: this.state.notes,
      name: this.state.name,
      isTest: this.state.isTest,
      debitorContraAccount: this.state.debitorContraAccount,
    };

    this.props.remoteDossier.api.update(dossier);
  }
}

DossierDetails.propTypes = {
  selectedPeriod: PropTypes.object,
  t: PropTypes.func.isRequired,
  dossier: PropTypes.object.isRequired,
  remoteDossier: RemoteDataPropType,
  remotePeriods: RemoteDataPropType,
};

export default consumesPeriods(consumesDossier(translate(DossierDetails)));
